/**
 * Outil « Parabole en trois formes » (Boîte à outils, 1re → Tle).
 *
 * On règle le trinôme par la forme qu'on veut — développée (a, b, c),
 * canonique (a, α, β) ou factorisée (a, x₁, x₂) — et les trois écritures
 * suivent, exactes (fractions, racines en √). La courbe montre le sommet,
 * l'axe de symétrie, les racines et le signe colorié sur l'axe des
 * abscisses ; Δ est calculé en clair et le tableau de signes se remplit.
 * Défi « Qui est f ? » : reconnaître l'expression d'une parabole tracée.
 * Calculs : `trinomial.ts`, défi : `quiz.ts`.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@jeraisonne/ui';
import { Note, PRIMARY_BUTTON, SECONDARY_BUTTON, Segmented, Stepper } from '../../ui/controls';
import { add, frac, value, type Frac } from '../../ui/exact';
import { makeFrame, type Frame } from '../../ui/frame';
import { frenchSpaces } from '../../ui/format';
import { seededRng } from '../../ui/random';
import { FIGURE_BG_CLASS, HaloText, PlotGrid } from '../../ui/svg-parts';
import { QUIZ_HINT, makeQuiz, type Quiz } from './quiz';
import {
  F,
  alpha,
  beta,
  canonicalText,
  deltaText,
  developedText,
  evalTri,
  factoredText,
  fr,
  fromCanonical,
  fromRoots,
  rootValues,
  roots,
  signRow,
  surdText,
  type Tri,
} from './trinomial';

const SIZE = 320;
const VP = { xMin: -6, xMax: 6, yMin: -6, yMax: 6 };
const CURVE = 'var(--color-primary-500)';
const AXIS_SYM = 'var(--color-accent-600)';
const PLUS = 'var(--color-success-600)';
const MINUS = 'var(--color-error-500)';
const ROUNDS = 5;

type Form = 'developed' | 'canonical' | 'factored';

const num = (v: number) => fr(F(v));

function parabolaPath(t: Tri, frame: Frame): string {
  const N = 240;
  let d = '';
  let pen = false;
  for (let i = 0; i <= N; i++) {
    const x = VP.xMin + ((VP.xMax - VP.xMin) * i) / N;
    const y = evalTri(t, x);
    if (y < VP.yMin - 4 || y > VP.yMax + 4) {
      pen = false;
      continue;
    }
    d += `${pen ? 'L' : 'M'}${frame.tx(x).toFixed(1)},${frame.ty(y).toFixed(1)} `;
    pen = true;
  }
  return d;
}

/** la parabole, son sommet, son axe, ses racines et le signe sur l'axe des abscisses */
function Figure({ t, ghost, label }: { t: Tri; ghost?: Tri | null; label: string }) {
  const frame = makeFrame(VP, SIZE);
  const { tx, ty } = frame;
  const al = value(alpha(t));
  const be = value(beta(t));
  const rs = rootValues(t);
  const y0 = ty(0);
  // morceaux de l'axe des abscisses : signe de f entre les racines
  const cuts = [VP.xMin, ...rs.filter((r) => r > VP.xMin && r < VP.xMax), VP.xMax];
  const pieces = cuts.slice(1).map((hi, i) => {
    const lo = cuts[i]!;
    return { lo, hi, plus: evalTri(t, (lo + hi) / 2) > 0 };
  });
  const sx = tx(al);
  const sy = ty(be);
  const inView = be >= VP.yMin && be <= VP.yMax && al >= VP.xMin && al <= VP.xMax;
  return (
    <svg
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      className={cn(
        'border-ink-100 mx-auto block aspect-square w-full max-w-[26rem] select-none rounded-2xl border',
        FIGURE_BG_CLASS,
      )}
      role="img"
      aria-label={label}
      data-testid="parabola-figure"
    >
      <PlotGrid frame={frame} step={1} />
      {pieces.map((p, i) => (
        <line
          key={i}
          x1={tx(p.lo)}
          x2={tx(p.hi)}
          y1={y0}
          y2={y0}
          stroke={p.plus ? PLUS : MINUS}
          strokeWidth={5}
          opacity={0.75}
        />
      ))}
      <line
        x1={sx}
        x2={sx}
        y1={0}
        y2={SIZE}
        stroke={AXIS_SYM}
        strokeWidth={1.5}
        strokeDasharray="6 5"
      />
      {ghost && (
        <path
          d={parabolaPath(ghost, frame)}
          fill="none"
          stroke={MINUS}
          strokeWidth={2.5}
          strokeDasharray="7 5"
        />
      )}
      <path d={parabolaPath(t, frame)} fill="none" stroke={CURVE} strokeWidth={3} />
      {rs.map((r, i) =>
        r >= VP.xMin && r <= VP.xMax ? (
          <circle key={i} cx={tx(r)} cy={y0} r={5} fill="var(--color-ink-900)" />
        ) : null,
      )}
      {inView && (
        <g>
          <circle cx={sx} cy={sy} r={5} fill={CURVE} />
          <HaloText
            x={Math.min(SIZE - 14, sx + 8)}
            y={be >= 0 ? Math.max(14, sy - 8) : Math.min(SIZE - 6, sy + 18)}
            fontSize={14}
            fontWeight={700}
            fill="var(--color-ink-900)"
          >
            S
          </HaloText>
        </g>
      )}
    </svg>
  );
}

/** tableau de signes : x, puis signe de f(x) entre les racines */
function SignTable({ t }: { t: Tri }) {
  const row = signRow(t);
  const W = 320;
  const H = 72;
  const X0 = 84;
  const X1 = W - 14;
  // valeur trop longue pour la colonne : x₁, x₂ (les valeurs sont écrites dans la note)
  const long = row.zeros.some((z) => z.length > 7);
  const names = row.zeros.length === 1 ? ['x₀'] : ['x₁', 'x₂'];
  const cols = ['−∞', ...(long ? names : row.zeros), '+∞'];
  const colX = (i: number) => X0 + (i * (X1 - X0)) / (cols.length - 1);
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className={cn(
        'border-ink-200 mx-auto block w-full max-w-[24rem] rounded-xl border',
        FIGURE_BG_CLASS,
      )}
      role="img"
      aria-label={
        row.zeros.length === 0
          ? `Tableau de signes : f(x) ${row.signs[0] === '+' ? 'positif' : 'négatif'} pour tout réel x.`
          : `Tableau de signes : ${row.signs
              .map(
                (s, i) =>
                  `${s === '+' ? 'positif' : 'négatif'}${i < row.zeros.length ? `, nul en ${row.zeros[i]}` : ''}`,
              )
              .join(', ')}.`
      }
      data-testid="parabola-signs"
    >
      <line x1={0} x2={W} y1={30} y2={30} stroke="var(--color-ink-300)" />
      <line x1={70} x2={70} y1={0} y2={H} stroke="var(--color-ink-300)" />
      <text
        x={35}
        y={20}
        textAnchor="middle"
        fontSize={13}
        fontStyle="italic"
        fill="var(--color-ink-700)"
      >
        x
      </text>
      <text
        x={35}
        y={56}
        textAnchor="middle"
        fontSize={13}
        fontStyle="italic"
        fill="var(--color-ink-700)"
      >
        f(x)
      </text>
      {cols.map((c, i) => (
        <text
          key={`c${i}`}
          x={colX(i)}
          y={20}
          textAnchor={i === 0 ? 'start' : i === cols.length - 1 ? 'end' : 'middle'}
          fontSize={13}
          fill="var(--color-ink-900)"
        >
          {c}
        </text>
      ))}
      {row.zeros.map((_, i) => (
        <g key={`z${i}`}>
          <line
            x1={colX(i + 1)}
            x2={colX(i + 1)}
            y1={30}
            y2={H}
            stroke="var(--color-ink-300)"
            strokeDasharray="3 3"
          />
          <text
            x={colX(i + 1)}
            y={56}
            textAnchor="middle"
            fontSize={13}
            fill="var(--color-ink-900)"
          >
            0
          </text>
        </g>
      ))}
      {row.signs.map((s, i) => (
        <text
          key={`s${i}`}
          x={(colX(i) + colX(i + 1)) / 2}
          y={57}
          textAnchor="middle"
          fontSize={18}
          fontWeight={700}
          fill={s === '+' ? PLUS : MINUS}
        >
          {s}
        </text>
      ))}
    </svg>
  );
}

function rootsText(t: Tri): string {
  const r = roots(t);
  if (r.kind === 'none')
    return `${deltaText(t)} < 0 : pas de racine réelle, f(x) a le signe de a pour tout réel x.`;
  if (r.kind === 'one') return `${deltaText(t)} : une racine double x₀ = −b/(2a) = ${fr(r.x0)}.`;
  // x₁ < x₂ : selon le signe de a, (−b − √Δ)/(2a) est la plus petite… ou la plus grande
  return `${deltaText(t)} > 0 : deux racines (−b ± √Δ)/(2a), x₁ = ${surdText(r.x1)} et x₂ = ${surdText(r.x2)} (x₁ < x₂).`;
}

/** racines rationnelles exactes, réglables au curseur ; null sinon */
function rationalRoots(t: Tri): [Frac, Frac] | null {
  const r = roots(t);
  if (r.kind === 'one') return [r.x0, r.x0];
  if (r.kind === 'two' && r.x1.B === 0 && r.x2.B === 0)
    return [frac(r.x1.A, r.x1.D), frac(r.x2.A, r.x2.D)];
  return null;
}

/** un cran de curseur ajouté à la valeur EXACTE (jamais d'arrondi) */
const nudge = (cur: Frac, v: number) => add(cur, F(v - value(cur)));

function Explore() {
  const [form, setForm] = useState<Form>('developed');
  const [t, setT] = useState<Tri>({ a: F(1), b: F(-2), c: F(-3) });
  const [notice, setNotice] = useState('');
  const a = value(t.a);
  const setA = (v: number, keep: (na: Frac) => Tri) => {
    // a ne vaut jamais 0 : on saute de −0,5 à 0,5
    const na = v === 0 ? (a > 0 ? -0.5 : 0.5) : v;
    setT(keep(F(na)));
  };
  const al = alpha(t);
  const be = beta(t);
  const rr = rationalRoots(t);

  const switchForm = (f: Form) => {
    setNotice('');
    // seule la forme factorisée peut demander de changer de fonction : racines irrationnelles ou absentes
    if (f === 'factored' && !rationalRoots(t)) {
      const c = Math.round(value(al) * 2) / 2;
      const next = fromRoots(t.a, F(c - 1), F(c + 1));
      const r = roots(t);
      setNotice(
        r.kind === 'none'
          ? `Δ < 0 : pas de racine. On part de x₁ = ${num(c - 1)} et x₂ = ${num(c + 1)}, de part et d’autre du sommet.`
          : `Racines ${r.kind === 'two' ? `${surdText(r.x1)} et ${surdText(r.x2)}` : ''} : pas réglables au curseur. On part de x₁ = ${num(c - 1)} et x₂ = ${num(c + 1)}.`,
      );
      setT(next);
    }
    setForm(f);
  };

  const aStepper = (keep: (na: Frac) => Tri) => (
    <Stepper
      label="a"
      value={a}
      min={-3}
      max={3}
      step={0.5}
      format={() => fr(t.a)}
      onChange={(v) => setA(v, keep)}
    />
  );
  const steppers =
    form === 'developed' ? (
      <>
        {aStepper((na) => ({ ...t, a: na }))}
        <Stepper
          label="b"
          value={value(t.b)}
          min={-8}
          max={8}
          step={0.5}
          format={() => fr(t.b)}
          onChange={(v) => setT({ ...t, b: nudge(t.b, v) })}
        />
        <Stepper
          label="c"
          value={value(t.c)}
          min={-8}
          max={8}
          step={0.5}
          format={() => fr(t.c)}
          onChange={(v) => setT({ ...t, c: nudge(t.c, v) })}
        />
      </>
    ) : form === 'canonical' ? (
      <>
        {aStepper((na) => fromCanonical(na, al, be))}
        <Stepper
          label="α"
          value={value(al)}
          min={-5}
          max={5}
          step={0.5}
          format={() => fr(al)}
          onChange={(v) => setT(fromCanonical(t.a, nudge(al, v), be))}
        />
        <Stepper
          label="β"
          value={value(be)}
          min={-6}
          max={6}
          step={0.5}
          format={() => fr(be)}
          onChange={(v) => setT(fromCanonical(t.a, al, nudge(be, v)))}
        />
      </>
    ) : rr ? (
      <>
        {aStepper((na) => fromRoots(na, rr[0], rr[1]))}
        {/* x₁ ne dépasse pas x₂ (et inversement) : les rôles ne s'échangent pas */}
        <Stepper
          label="x₁"
          value={value(rr[0])}
          min={-5}
          max={value(rr[1])}
          step={0.5}
          format={() => fr(rr[0])}
          onChange={(v) => setT(fromRoots(t.a, nudge(rr[0], v), rr[1]))}
        />
        <Stepper
          label="x₂"
          value={value(rr[1])}
          min={value(rr[0])}
          max={5}
          step={0.5}
          format={() => fr(rr[1])}
          onChange={(v) => setT(fromRoots(t.a, rr[0], nudge(rr[1], v)))}
        />
      </>
    ) : null;

  const fact = factoredText(t);
  const line = (f: Form, name: string, text: string) => (
    <p
      className={cn(
        'rounded-xl px-3 py-1.5 text-sm',
        form === f ? 'bg-primary-50 text-ink-900 font-semibold' : 'text-ink-700',
      )}
    >
      <span className="text-ink-600 mr-1 text-xs font-semibold uppercase">{name}</span>{' '}
      {frenchSpaces(text)}
    </p>
  );

  return (
    <div className="space-y-3">
      <Segmented
        ariaLabel="Je règle la forme"
        value={form}
        options={[
          { value: 'developed', label: 'Développée' },
          { value: 'canonical', label: 'Canonique' },
          { value: 'factored', label: 'Factorisée' },
        ]}
        onChange={switchForm}
      />
      {notice && <Note live>{frenchSpaces(notice)}</Note>}
      <Figure
        t={t}
        label={`Parabole de sommet S(${fr(al)} ; ${fr(be)}), tournée vers le ${a > 0 ? 'haut' : 'bas'}.`}
      />
      <div className="grid gap-2 sm:grid-cols-3">{steppers}</div>
      <div className="space-y-1" aria-live="polite" data-testid="parabola-forms">
        {line('developed', 'Développée', `f(x) = ${developedText(t)}`)}
        {line('canonical', 'Canonique', `f(x) = ${canonicalText(t)}`)}
        {line(
          'factored',
          'Factorisée',
          fact ? `f(x) = ${fact}` : 'pas de forme factorisée dans ℝ (Δ < 0)',
        )}
      </div>
      <p className="text-ink-700 text-sm">
        {frenchSpaces(
          `Sommet S(${fr(al)} ; ${fr(be)}), axe de symétrie x = ${fr(al)} ; parabole tournée vers le ${a > 0 ? 'haut (a > 0)' : 'bas (a < 0)'}.`,
        )}
      </p>
      <Note live={false}>{frenchSpaces(rootsText(t))}</Note>
      <SignTable t={t} />
    </div>
  );
}

/** ce qu'un élève voyant lit sur la figure du défi */
function quizLabel(q: Quiz): string {
  const up = value(q.tri.a) > 0 ? 'vers le haut' : 'vers le bas';
  if (q.form === 'canonical') {
    const al = value(alpha(q.tri));
    return `Parabole tournée ${up}, de sommet (${fr(alpha(q.tri))} ; ${fr(beta(q.tri))}), passant par (${num(al + 1)} ; ${num(evalTri(q.tri, al + 1))}).`;
  }
  const rs = rootValues(q.tri);
  return `Parabole tournée ${up}, qui coupe l’axe des abscisses en ${rs.map((x) => num(x)).join(' et ')} et l’axe des ordonnées en ${fr(q.tri.c)}.`;
}

function Challenge({ seed }: { seed?: number }) {
  const rnd = useMemo(() => (seed === undefined ? Math.random : seededRng(seed)), [seed]);
  const [q, setQ] = useState<Quiz>(() => makeQuiz(rnd));
  const [picked, setPicked] = useState<number | null>(null);
  const [count, setCount] = useState(1);
  const [score, setScore] = useState(0);
  const done = picked !== null && count >= ROUNDS;
  const chosen = picked === null ? null : q.options[picked]!;
  const right = q.options.find((o) => o.correct)!;
  const titleRef = useRef<HTMLParagraphElement>(null);
  const moved = useRef(false);
  // la nouvelle consigne prend le focus (le bouton « Question suivante » a disparu)
  useEffect(() => {
    if (moved.current) titleRef.current?.focus();
    moved.current = false;
  }, [q]);

  const pick = (i: number) => {
    if (picked !== null) return;
    setPicked(i);
    if (q.options[i]!.correct) setScore(score + 1);
  };
  const next = () => {
    if (done) {
      setCount(1);
      setScore(0);
    } else setCount(count + 1);
    moved.current = true;
    setQ(makeQuiz(rnd));
    setPicked(null);
  };

  return (
    <div className="space-y-3">
      <p className="text-ink-500 text-xs font-semibold uppercase tracking-wide">
        Question {count} / {ROUNDS} · {score} juste{score > 1 ? 's' : ''}
      </p>
      <p ref={titleRef} tabIndex={-1} className="text-ink-900 text-base font-semibold outline-none">
        {q.form === 'canonical'
          ? 'Voici la parabole de f. Quelle est sa forme canonique ?'
          : 'Voici la parabole de f. Quelle est sa forme factorisée ?'}
      </p>
      <Figure
        t={q.tri}
        ghost={chosen && !chosen.correct ? chosen.tri : null}
        label={quizLabel(q)}
      />
      {chosen && !chosen.correct && (
        <p className="text-ink-600 text-xs">
          {frenchSpaces('En pointillés rouges : la parabole de la réponse choisie.')}
        </p>
      )}
      <div role="group" aria-label="Réponses" className="grid gap-2">
        {q.options.map((o, i) => (
          <button
            key={o.text}
            type="button"
            onClick={() => pick(i)}
            aria-pressed={picked === i}
            aria-disabled={picked !== null && picked !== i}
            className={cn(
              'min-h-11 rounded-xl border px-3 py-2 text-left text-sm font-semibold transition-colors',
              picked === null && 'border-ink-200 text-ink-800 hover:bg-ink-50',
              picked !== null && o.correct && 'border-success-600 bg-success-50 text-ink-900',
              picked === i && !o.correct && 'border-error-500 bg-error-50 text-ink-900',
              picked !== null && picked !== i && !o.correct && 'border-ink-100 text-ink-500',
            )}
          >
            {frenchSpaces(`f(x) = ${o.text}`)}
          </button>
        ))}
      </div>
      {chosen && (
        <Note live tone={chosen.correct ? 'success' : 'error'}>
          {frenchSpaces(
            chosen.correct
              ? q.form === 'canonical'
                ? `Exact : sommet S(${fr(alpha(q.tri))} ; ${fr(beta(q.tri))}), f(x) = ${developedText(q.tri)}.`
                : `Exact : racines ${rootValues(q.tri)
                    .map((x) => fr(F(x)))
                    .join(' et ')}, f(0) = ${fr(q.tri.c)} ; f(x) = ${developedText(q.tri)}.`
              : `${QUIZ_HINT[chosen.mistake ?? 'alpha']} La bonne réponse : f(x) = ${right.text}.`,
          )}
        </Note>
      )}
      {picked !== null && (
        <div className="flex flex-wrap items-center gap-3">
          {done && (
            <p className="text-ink-900 text-sm font-semibold">{`Score : ${score} / ${ROUNDS}`}</p>
          )}
          <button type="button" className={done ? SECONDARY_BUTTON : PRIMARY_BUTTON} onClick={next}>
            {done ? 'Recommencer' : 'Question suivante'}
          </button>
        </div>
      )}
    </div>
  );
}

export function ParabolaTool({ seed }: { /** graine du défi (tests) */ seed?: number }) {
  const [mode, setMode] = useState<'explore' | 'challenge'>('explore');
  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Ce qu’on fait"
        value={mode}
        options={[
          { value: 'explore', label: 'Explorer' },
          { value: 'challenge', label: frenchSpaces('Qui est f ?') },
        ]}
        onChange={setMode}
      />
      <div hidden={mode !== 'explore'}>
        <Explore />
      </div>
      <div hidden={mode !== 'challenge'}>
        <Challenge seed={seed} />
      </div>
    </div>
  );
}
